/* Copyright (c) Trimble Inc. All rights reserved. */
import { dropDatabase, Logger, Table, type TableInterface } from '@tcp-base/toolkit';
// Define interfaces for the stored data
export interface StoredToken {
  key: string;
  value: string | number;
}

export interface StoredUser {
  key: string;
  value: string;
}

const DB_NAME = 'auth_store';
// Define the database class using native IndexedDB
export class AuthDatabase {
  private db: IDBDatabase | null = null;
  private _token: Table<StoredToken> | null = null;
  private _user: Table<StoredUser> | null = null;

  constructor() {
    // Database will be opened when needed
  }

  /**
   * Open the IndexedDB database
   */
  async open(): Promise<void> {
    if (this.db) {
      return; // Already opened
    }

    return new Promise((resolve: () => void, reject: (error: Error) => void): void => {
      const request = indexedDB.open(DB_NAME, 21);

      request.onerror = (): void => {
        Logger.error('AuthDatabase: Failed to open database:', request.error);
        reject(request.error ?? new Error('Failed to open database'));
      };

      request.onsuccess = (): void => {
        this.db = request.result;

        // Set up event handlers
        this.db.onversionchange = (): void => {
          Logger.warn('AuthDatabase: Database version changed by another tab');
          this.close();
        };

        // Create table instances
        this._token = new Table<StoredToken>(this.db, 'token');
        this._user = new Table<StoredUser>(this.db, 'user');

        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        if (oldVersion < 1) {
          // Version 1: Create stores with keyPath (old format)
          if (!db.objectStoreNames.contains('token')) {
            db.createObjectStore('token', { keyPath: 'key' });
          }

          if (!db.objectStoreNames.contains('user')) {
            db.createObjectStore('user', { keyPath: 'key' });
          }
        }

        if (oldVersion < 2) {
          // Version 2: Recreate stores without keyPath for direct key-value storage
          if (db.objectStoreNames.contains('token')) {
            db.deleteObjectStore('token');
          }
          if (db.objectStoreNames.contains('user')) {
            db.deleteObjectStore('user');
          }

          db.createObjectStore('token');
          db.createObjectStore('user');
        }
      };

      request.onblocked = (): void => {
        Logger.warn('AuthDatabase: Database is blocked - attempting to resolve...');
        setTimeout(() => {
          Logger.log('AuthDatabase: Retrying database connection...');
          // The request will continue once unblocked
        }, 1000);
      };
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this._token = null;
      this._user = null;
    }
  }

  /**
   * Get token table for operations
   */
  getTokenTable(): TableInterface<StoredToken> {
    if (!this._token) {
      throw new Error('Database not opened. Call open() first.');
    }
    return this._token;
  }

  /**
   * Get user table for operations
   */
  getUserTable(): TableInterface<StoredUser> {
    if (!this._user) {
      throw new Error('Database not opened. Call open() first.');
    }
    return this._user;
  }

  /**
   * Get the token table property (for compatibility with existing code)
   */
  get token(): TableInterface<StoredToken> {
    return this.getTokenTable();
  }

  /**
   * Get the user table property (for compatibility with existing code)
   */
  get user(): TableInterface<StoredUser> {
    return this.getUserTable();
  }
}

// Database connection state
let dbInstance: AuthDatabase | null = null;

/**
 * Get the database instance (singleton pattern)
 */
export const getDatabase: () => Promise<AuthDatabase> = async (): Promise<AuthDatabase> => {
  if (!dbInstance) {
    dbInstance = new AuthDatabase();
    try {
      await dbInstance.open();
    } catch (error) {
      Logger.error('AuthDatabase: Failed to open database:', error);
      throw error;
    }
  }
  return dbInstance;
};

/**
 * Drop the database
 */
export const dropAuthDatabase: () => Promise<void> = async (): Promise<void> => {
  try {
    await dropDatabase(DB_NAME);
    Logger.log('AuthDatabase: Database dropped successfully');
    dbInstance = null; // Reset the singleton instance
  } catch (error) {
    Logger.error('AuthDatabase: Failed to drop database:', error);
    throw error;
  }
};

/**
 * Helper functions to get tables directly
 */
export const getTokenTable: () => Promise<TableInterface<StoredToken>> = async (): Promise<
  TableInterface<StoredToken>
> => {
  const db = await getDatabase();
  return db.getTokenTable();
};

export const getUserTable: () => Promise<TableInterface<StoredUser>> = async (): Promise<
  TableInterface<StoredUser>
> => {
  const db = await getDatabase();
  return db.getUserTable();
};
